import { IconButton, Link, Stack, Tooltip } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { forwardRef, ReactNode, Ref } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { GlobalSearchEvents } from '@/components/global-search/machines/globalSearchMachine';
import { SearchResultItem } from '@/components/global-search/type';
import Iconify from '@/components/Iconify';
import { ICON_SIZE_MAP } from '@/config';
import usePosthogTracking from '@/hooks/usePosthogTracking';

type Props = {
  title: ReactNode;
  isSelected?: boolean;
  displayBuildingPath?: ReactNode;
  customerExternalIdentifier?: ReactNode;
  selectedNavigationOptionIndex?: number;
  ref?: Ref<HTMLAnchorElement>;
  item: SearchResultItem;
  send: (event: GlobalSearchEvents) => void;
  query: string;
};

const GlobalSearchResultItem = forwardRef<HTMLAnchorElement, Props>(function GlobalSearchResultItem(props, ref) {
  const {
    title,
    isSelected = false,
    displayBuildingPath,
    customerExternalIdentifier,
    selectedNavigationOptionIndex = 0,
    item,
    send,
    query,
  } = props;
  const { trackEvent } = usePosthogTracking();
  const navigationOptions = item.navigationOptions ?? [];
  const hasNavigationOptions = navigationOptions.length > 0;

  const secondaryContent = customerExternalIdentifier ? (
    <>
      {customerExternalIdentifier} • {displayBuildingPath}
    </>
  ) : (
    displayBuildingPath
  );

  const navigate = useNavigate();

  return (
    <Link
      component={RouterLink}
      to={item.path}
      onClick={() => {
        trackEvent('GLOBAL_SEARCH_RESULT_CLICKED', {
          title: item.title,
          path: item.path,
          search_query: query,
          type: item.type,
        });
        send({ type: 'CLOSE' });
      }}
      tabIndex={0}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
          textDecoration: 'none',
        },
        '&:focus': {
          outline: 'none',
        },
      }}
      id={isSelected ? 'global-search-result-selected-item' : ``}
      ref={ref}
    >
      <ListItemButton
        selected={isSelected}
        sx={{
          py: 0.5,
          my: 0.5,
          borderRadius: 1,
          minHeight: 56,
        }}
      >
        {item.icon ? (
          <Stack sx={{ mr: 1.5, alignItems: 'center', ml: -0.5 }}>
            <Iconify icon={item.icon} width={ICON_SIZE_MAP.md} sx={{ color: 'primary.main' }} />
          </Stack>
        ) : null}

        <ListItemText
          primaryTypographyProps={{
            typography: 'subtitle2',
          }}
          primary={
            <PTypography
              variant={'body2'}
              noOverflow={{ maxWidth: 300, title: typeof title === 'string' ? title : '' }}
            >
              {title}
            </PTypography>
          }
          secondaryTypographyProps={{
            typography: 'body2',
            color: 'text.secondary',
          }}
          secondary={secondaryContent}
        />

        {hasNavigationOptions ? (
          <Stack direction="row" gap={0.5}>
            {navigationOptions.map((option, index) => (
              <Tooltip key={index} title={option.label} arrow>
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    trackEvent('GLOBAL_SEARCH_RESULT_CLICKED', {
                      title: item.title,
                      path: item.path,
                      search_query: query,
                      type: item.type,
                    });
                    navigate(option.path);
                    send({ type: 'CLOSE' });
                  }}
                  sx={{
                    border: '1px solid',
                    borderColor: selectedNavigationOptionIndex === index && isSelected ? 'primary.main' : 'divider',
                    backgroundColor:
                      selectedNavigationOptionIndex === index && isSelected ? 'action.selected' : 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderColor: 'primary.main',
                    },
                  }}
                  id={`global-search-result-sub-navigation-option-${index}`}
                >
                  <Iconify icon={option.icon} width={ICON_SIZE_MAP.smPlus} height={ICON_SIZE_MAP.smPlus} />
                </IconButton>
              </Tooltip>
            ))}
          </Stack>
        ) : null}
      </ListItemButton>
    </Link>
  );
});

export default GlobalSearchResultItem;
